import { Check, ChevronsUpDown } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useProjectStore } from "@/stores/project.store";
import { cn } from "@/lib/utils";

interface ProjectSelectorProps {
  collapsed?: boolean;
}

export function ProjectSelector({ collapsed = false }: ProjectSelectorProps) {
  const navigate = useNavigate();
  const params = useParams<{ slug?: string }>();
  const projects = useProjectStore((s) => s.projects);
  const currentProject = useProjectStore((s) => s.currentProject);
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);

  const active = currentProject ?? null;

  const handleSelect = (slug: string) => {
    const project = projects.find((p) => p.slug === slug) ?? null;
    setCurrentProject(project);
    if (project) {
      const targetSegment = params.slug ? "dashboard" : "dashboard";
      navigate(`/projects/${project.slug}/${targetSegment}`);
    }
  };

  if (collapsed) {
    return (
      <div
        className="mx-auto flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5"
        title={active?.name ?? "No project"}
      >
        <span className="h-2 w-2 rounded-full bg-indigo-500" />
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            "flex w-full items-center justify-between rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-left transition-colors",
            "hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-indigo-500/40",
          )}
        >
          <div className="flex min-w-0 items-center gap-3">
            <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
            <div className="min-w-0">
              <div className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                Project
              </div>
              <div className="truncate text-sm font-medium text-white">
                {active?.name ?? "No project"}
              </div>
            </div>
          </div>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={6}
        className="w-[228px]"
      >
        <DropdownMenuLabel>Projects</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {projects.length === 0 ? (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            No projects available
          </div>
        ) : (
          projects.map((p) => (
            <DropdownMenuItem
              key={p.slug}
              onSelect={() => handleSelect(p.slug)}
              className="flex items-center justify-between gap-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <span className="h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                <span className="truncate">{p.name}</span>
              </div>
              {active?.slug === p.slug && (
                <Check className="h-4 w-4 text-indigo-500" />
              )}
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
